import React, { createContext, useContext, useState, useCallback } from "react";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { getItem } from "@/lib/api";
import { fmtQty, fmtMoney, fmtLt, fmtCover, fmtNum } from "@/lib/format";
import { statusMeta, actionClass } from "@/lib/statusMeta";
import { Badge } from "@/components/ui/badge";

const Ctx = createContext(null);
export const useItemDetail = () => useContext(Ctx);

const Row = ({ label, value, mono = true }) => (
  <div className="flex items-baseline justify-between py-1.5 border-b border-[var(--line)] last:border-0">
    <span className="label-eyebrow">{label}</span>
    <span className={`text-sm font-medium text-slate-800 ${mono ? "num" : ""}`}>{value}</span>
  </div>
);

export function ItemDetailProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const openItem = useCallback(async (itemNo) => {
    setOpen(true);
    setLoading(true);
    setData(null);
    try {
      const d = await getItem(itemNo);
      setData(d);
    } catch (e) {
      setData({ error: true });
    } finally {
      setLoading(false);
    }
  }, []);

  const i = data?.item;
  const sm = i ? statusMeta(i.status) : null;

  return (
    <Ctx.Provider value={{ openItem }}>
      {children}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto slim p-0" data-testid="item-detail-drawer">
          {loading && <div className="p-8 text-sm text-muted-foreground">Loading item…</div>}
          {data?.error && <div className="p-8 text-sm text-red-600">Item not found.</div>}
          {i && (
            <div>
              <div className="px-6 pt-6 pb-4 border-b border-[var(--line)]">
                <div className="label-eyebrow">Item Detail</div>
                <div className="flex items-center gap-3 mt-1">
                  <h2 className="text-2xl font-bold num text-slate-900" data-testid="detail-item-no">{i.item_no}</h2>
                  <Badge variant="outline" className={`${sm.badge} rounded-full`}>{sm.label}</Badge>
                </div>
                <p className="text-sm text-slate-600 mt-1">{i.desc} {i.desc2 ? `· ${i.desc2}` : ""}</p>
                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-slate-500">
                  <span>{i.klass}</span>
                  <span>{i.subclass}</span>
                  <span>UOM {i.uom}</span>
                  <span>{i.pref_supp}</span>
                </div>
              </div>

              <div className="px-6 py-4">
                <div className={`rounded-lg border p-3 mb-4 ${sm.badge}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="label-eyebrow" style={{ color: "inherit" }}>Recommendation</span>
                    <span className={`text-[11px] font-semibold px-2 py-0.5 rounded ${actionClass(i.recommended_action)}`}>{i.recommended_action}</span>
                  </div>
                  <p className="text-[13px] leading-relaxed">{i.reason}</p>
                </div>

                <div className="grid grid-cols-2 gap-x-6">
                  <Row label="Total SOH" value={fmtQty(i.soh)} />
                  <Row label="SOH Value" value={fmtMoney(i.soh_value)} />
                  <Row label="Avg Consumption /mo" value={fmtQty(i.consumption)} />
                  <Row label="Total Open PO" value={fmtQty(i.open_po)} />
                  <Row label="Total In Receipt" value={fmtQty(i.in_receipt)} />
                  <Row label="Total In Transit" value={fmtQty(i.in_transit)} />
                  <Row label="Total Available Supply" value={fmtQty(i.total_supply)} />
                  <Row label="Average Cost" value={fmtMoney(i.avg_cost)} />
                  <Row label="Source Lead Time" value={fmtLt(i.source_lt)} />
                  <Row label="Manual Lead Time" value={fmtLt(i.manual_lt)} />
                  <Row label="Effective Lead Time" value={fmtLt(i.effective_lt)} />
                  <Row label="SOH Months Cover" value={fmtCover(i.soh_months_cover)} />
                  <Row label="Supply Months Cover" value={fmtCover(i.supply_months_cover)} />
                  <Row label="Min / Max" value={`${fmtQty(i.min_qty)} / ${fmtQty(i.max_qty)}`} />
                  <Row label="PO Required" value={i.po_required ? "YES" : "NO"} />
                  <Row label="Recommended PO Qty" value={fmtQty(i.recommended_po_qty)} />
                  <Row label="Recommended PO Value" value={fmtMoney(i.recommended_po_value)} />
                </div>

                <div className="label-eyebrow mt-6 mb-2">Warehouse Breakdown</div>
                <div className="overflow-x-auto slim border border-[var(--line)] rounded-lg">
                  <table className="w-full text-[13px]">
                    <thead className="bg-[var(--surface)]">
                      <tr>
                        {["Warehouse", "SOH", "SOH Value", "Open PO", "In Rec", "In Tr", "Min", "Max"].map((h) => (
                          <th key={h} className="thead-cell text-left px-3 py-2">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {data.warehouses.map((w, idx) => (
                        <tr key={idx} className="border-t border-[var(--line)]">
                          <td className="px-3 py-2 font-medium">{w.warehouse}</td>
                          <td className="px-3 py-2 num">{fmtQty(w.soh)}</td>
                          <td className="px-3 py-2 num">{fmtMoney(w.soh_value)}</td>
                          <td className="px-3 py-2 num">{fmtQty(w.open_po)}</td>
                          <td className="px-3 py-2 num">{fmtQty(w.in_receipt)}</td>
                          <td className="px-3 py-2 num">{fmtQty(w.in_transit)}</td>
                          <td className="px-3 py-2 num">{fmtQty(w.min_qty)}</td>
                          <td className="px-3 py-2 num">{fmtQty(w.max_qty)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </Ctx.Provider>
  );
}
